import { useEffect, useRef, useState } from "react";

const YOUTUBE_IFRAME_API_URL = "https://www.youtube.com/iframe_api";

const loadYouTubeIframeApi = () => {
  return new Promise((resolve, reject) => {
    if (typeof window === "undefined") {
      reject(new Error("window is not available"));
      return;
    }

    if (window.YT && window.YT.Player) {
      resolve(window.YT);
      return;
    }

    if (document.getElementById("youtube-iframe-api")) {
      const checkReady = () => {
        if (window.YT && window.YT.Player) {
          resolve(window.YT);
        } else {
          setTimeout(checkReady, 100);
        }
      };
      checkReady();
      return;
    }

    const existingOnYouTubeIframeAPIReady = window.onYouTubeIframeAPIReady;

    window.onYouTubeIframeAPIReady = () => {
      if (existingOnYouTubeIframeAPIReady) {
        existingOnYouTubeIframeAPIReady();
      }
      resolve(window.YT);
    };

    const tag = document.createElement("script");
    tag.id = "youtube-iframe-api";
    tag.src = YOUTUBE_IFRAME_API_URL;
    tag.async = true;
    tag.onerror = reject;
    document.body.appendChild(tag);
  });
};

/**
 * Load the YouTube IFrame API and create a player in the provided container.
 * Returns the player instance once it is ready.
 *
 * @param {string} videoId - YouTube video id
 * @param {object} options - additional player options
 */
export const useYouTubePlayer = (videoId, options = {}) => {
  const { onStateChange, playerVars = {}, events = {} } = options;
  const containerRef = useRef(null);
  const playerRef = useRef(null);
  const [isReady, setIsReady] = useState(false);
  const lastVideoIdRef = useRef(null);

  // Keep the latest callbacks/options in refs so they don't trigger player recreation.
  const onStateChangeRef = useRef(onStateChange);
  const playerVarsRef = useRef(playerVars);
  const eventsRef = useRef(events);

  useEffect(() => {
    onStateChangeRef.current = onStateChange;
  }, [onStateChange]);

  useEffect(() => {
    playerVarsRef.current = playerVars;
  }, [playerVars]);

  useEffect(() => {
    eventsRef.current = events;
  }, [events]);

  useEffect(() => {
    if (!videoId || !containerRef.current) return;
    if (videoId === lastVideoIdRef.current && playerRef.current) return;

    let isCancelled = false;

    loadYouTubeIframeApi()
      .then((YT) => {
        if (isCancelled || !containerRef.current) return;

        if (playerRef.current) {
          playerRef.current.destroy();
        }

        const origin = typeof window !== "undefined" ? window.location.origin : "";
        console.log("[useYouTubePlayer] creating player", { videoId, origin, container: containerRef.current });

        // Create the iframe ourselves so we can set Permissions-Policy allow attributes before it loads.
        const container = containerRef.current;
        container.innerHTML = "";
        const iframe = document.createElement("iframe");
        iframe.src = `https://www.youtube.com/embed/${videoId}?enablejsapi=1&origin=${encodeURIComponent(
          origin
        )}&playsinline=1&rel=0`;
        iframe.width = "100%";
        iframe.height = "100%";
        iframe.title = "YouTube video player";
        iframe.style.border = "0";
        iframe.setAttribute(
          "allow",
          "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; compute-pressure"
        );
        iframe.setAttribute("allowfullscreen", "true");
        container.appendChild(iframe);
        lastVideoIdRef.current = videoId;

        playerRef.current = new YT.Player(iframe, {
          events: {
            onReady: (event) => {
              console.log("[useYouTubePlayer] player ready", event.target.getIframe()?.src);
              if (!isCancelled) setIsReady(true);
            },
            onStateChange: (data) => onStateChangeRef.current?.(data),
            ...eventsRef.current,
          },
        });
      })
      .catch((err) => {
        // Fail silently — tracking should not break video playback.
        console.error("Failed to load YouTube API:", err);
      });

    return () => {
      isCancelled = true;
      setIsReady(false);
      try {
        playerRef.current?.destroy?.();
      } catch (e) {
        // Player may already been destroyed.
      }
      playerRef.current = null;
      lastVideoIdRef.current = null;
    };
  }, [videoId]);

  return { containerRef, playerRef, isReady };
};

export default useYouTubePlayer;
